import React, { useMemo } from 'react';
import { Box, Typography, useTheme, alpha } from '@mui/material';
import { ResponsiveLine } from '@nivo/line';

/**
 * TreeSHAP dependence (downsampled quantile bins): driver value on x,
 * marginal effect on the prediction on y (spec §7 J3 — EffectShapeChart).
 */
const EffectShapeChart = ({ shape = [], direction, height = 180 }) => {
  const theme = useTheme();
  const series = useMemo(() => ([{
    id: 'effect',
    data: (shape || [])
      .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.effect))
      .map((p) => ({ x: p.x, y: p.effect })),
  }]), [shape]);

  if (!series[0].data.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        No effect shape recorded for this edge.
      </Typography>
    );
  }

  const color = direction === 'negative'
    ? theme.palette.error.main
    : theme.palette.success.main;

  return (
    <Box sx={{ height }}>
      <ResponsiveLine
        data={series}
        margin={{ top: 8, right: 12, bottom: 36, left: 48 }}
        xScale={{ type: 'linear', min: 'auto', max: 'auto' }}
        yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
        curve="monotoneX"
        colors={[color]}
        lineWidth={2}
        enablePoints={false}
        enableArea
        areaOpacity={0.08}
        axisBottom={{
          legend: 'driver value',
          legendPosition: 'middle',
          legendOffset: 28,
          tickValues: 5,
          tickSize: 0,
          format: '.2~s',
        }}
        axisLeft={{
          legend: 'effect on forecast',
          legendPosition: 'middle',
          legendOffset: -40,
          tickValues: 4,
          tickSize: 0,
          format: '.2~s',
        }}
        markers={[{
          axis: 'y', value: 0,
          lineStyle: { stroke: alpha(theme.palette.text.primary, 0.25), strokeDasharray: '4 4' },
        }]}
        animate={false}
        useMesh
        theme={{
          text: { fill: theme.palette.text.secondary, fontSize: 10 },
          grid: { line: { stroke: alpha(theme.palette.text.primary, 0.06) } },
        }}
        tooltip={({ point }) => (
          <Box sx={{ p: 0.75, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 3 }}>
            <Typography variant="caption">
              x = {Number(point.data.x).toPrecision(3)} → effect {Number(point.data.y).toPrecision(3)}
            </Typography>
          </Box>
        )}
      />
    </Box>
  );
};

export default EffectShapeChart;
